
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, UserCheck, UserX } from 'lucide-react';
import { Courier } from '@/services/types';

interface CourierStatsCardProps {
  couriers: Courier[];
  totalCount: number;
  loading?: boolean;
}

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  format?: (value: number) => string;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ 
  value, 
  duration = 1000, 
  format = (v) => v.toString() 
}) => {
  const [displayValue, setDisplayValue] = React.useState(0);

  React.useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(easeOutQuart * value));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return <span>{format(displayValue)}</span>;
};

export const CourierStatsCard: React.FC<CourierStatsCardProps> = ({ 
  couriers, 
  totalCount, 
  loading = false 
}) => {
  const stats = React.useMemo(() => {
    const activeCouriers = couriers.filter(c => c.status === 'active').length;
    const inactiveCouriers = couriers.filter(c => c.status === 'inactive').length;
    const utilization = totalCount > 0 ? (activeCouriers / totalCount) * 100 : 0;

    return {
      activeCouriers,
      inactiveCouriers,
      utilization
    };
  }, [couriers, totalCount]);

  const formatNumber = (num: number) => 
    new Intl.NumberFormat('ru-RU').format(num);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Статистика курьеров
          </CardTitle>
          <CardDescription>Загрузка статистики...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-muted animate-pulse rounded" />
                <div className="h-6 bg-muted animate-pulse rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Статистика курьеров
        </CardTitle>
        <CardDescription>
          Общая информация по {formatNumber(totalCount)} курьерам
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-6">
          {/* Всего курьеров */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              Всего
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              <AnimatedCounter value={totalCount} />
            </div>
            <div className="text-xs text-muted-foreground">
              зарегистрированных курьеров
            </div>
          </div>

          {/* Активные курьеры */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <UserCheck className="h-4 w-4" />
              Активные
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-green-600">
                <AnimatedCounter value={stats.activeCouriers} />
              </span>
              <Badge variant="outline" className="text-green-600 border-green-200">
                <AnimatedCounter 
                  value={stats.utilization} 
                  format={(v) => `${v.toFixed(1)}%`}
                />
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              от общего количества
            </div>
          </div>

          {/* Неактивные курьеры */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <UserX className="h-4 w-4" />
              Неактивные
            </div>
            <div className="text-2xl font-bold text-gray-600">
              <AnimatedCounter value={stats.inactiveCouriers} />
            </div>
            <div className="text-xs text-muted-foreground">
              требуют активации
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
